import { Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import FooterLogos from './components/FooterLogos';
import FooterMenu from './components/FooterMenu';
import ScrollToTop from './components/ScrollToTop';
import LanguageOverHistory from './components/LanguageOverHistory';
import CookieBanner from './components/CookieBanner';
import MarkdownPage from './pages/MarkdownPage';
import InformationIndexPage from './pages/InformationIndexPage';
import CollectionsPage from './pages/CollectionsPage';
import SitemapPage from './pages/SitemapPage';
import ErrorPage from './pages/ErrorPage';
import ErrorBoundary from './components/ErrorBoundary';
import { useTheme } from './hooks/useTheme';
import { useAccessibility } from './hooks/useAccessibility';
import { useCookieConsent } from './hooks/useCookieConsent';
import { useDocumentLang } from './hooks/useDocumentMeta';
import { useLanguage } from './hooks/useLanguage';
import { languages, defaultLanguage } from './config/languages';
import { getPreferredLanguage } from './utils/languagePreference';

// Preserve old bookmarks/SEO: /themes/collections/123 → /en/ukwa/collection/123.
// A component is needed (rather than a bare <Navigate>) so the :collectionId
// param can be interpolated into the canonical target.
function LegacyCollectionRedirect() {
  const { collectionId } = useParams();
  const target = collectionId ? `/en/ukwa/collection/${collectionId}` : '/en/ukwa/collection';
  return <Navigate to={target} replace />;
}

// Preserve old bookmarks/SEO: the nominate page and the whole Information
// section moved under the uniform /:lang/ukwa/info namespace.
//   /:lang/save-website        → /:lang/ukwa/info/nominate
//   /:lang/information[/:page] → /:lang/ukwa/info[/:page]
function LegacyNominateRedirect() {
  const { lang } = useParams();
  return <Navigate to={`/${lang}/ukwa/info/nominate`} replace />;
}
function LegacyInfoRedirect() {
  const { lang, page } = useParams();
  const base = `/${lang}/ukwa/info`;
  return <Navigate to={page ? `${base}/${page}` : base} replace />;
}

// Known pre-/en English URLs (v0.1 shipped English at the site root). These
// permanently redirect to their canonical /en/… equivalent. Anything not in
// this set is treated as a genuine unknown URL and gets a 404 — we never
// silently forward to the home page.
const LEGACY_EN_PATHS = new Set([
  'themes', 'save-website', 'contact', 'about', 'information',
  'information/faq', 'information/accessibility', 'information/terms',
  'information/technical-information', 'information/notice-and-takedown',
  'information/sitemap',
]);

// Catch-all for anything the explicit routes did not match.
function LocaleFallback() {
  const { pathname } = useLocation();
  const segments = pathname.split('/').filter(Boolean);
  const first = segments[0];

  // A valid locale with an unmatched sub-path (e.g. /en/does-not-exist) → 404
  // rendered in that locale.
  if (Object.prototype.hasOwnProperty.call(languages, first)) {
    return <ErrorPage code={404} langCode={first} />;
  }

  // A known legacy unprefixed English page → permanent redirect to /en/…
  const bare = segments.join('/');
  if (LEGACY_EN_PATHS.has(bare)) {
    return <Navigate to={`/en/${bare}`} replace />;
  }

  // Genuinely unknown URL → 404 in the default locale.
  return <ErrorPage code={404} langCode={defaultLanguage} />;
}

function infoCrumbs(lang) {
  return [
    { label: lang.homeLabel, path: lang.urlPrefix || '/' },
    { label: lang.information.label, path: lang.information.path },
  ];
}

// "Skip to main content" goes to the page's own <main>, past the breadcrumb
// stripe that every page renders above it inside #main-content (OA bug #20,
// RRO bug #31), so the next Tab reaches the page content. Pages without a
// <main> yet (loading, errors) fall back to the #main-content container; the
// plain #main-content href keeps working without JavaScript.
// Focus moves without the browser's own scroll: that put <main> just below the
// scroll-padding and left the breadcrumb stripe behind the sticky header, an
// empty blue band with its text hidden (Safari macOS / iOS, Chrome too).
// Instead the stripe is kept in view directly under the header, so "You are
// here" stays readable while focus is on the page content.
function skipToMain(e) {
  const container = document.getElementById('main-content');
  const target = container?.querySelector('main') || container;
  if (!target) return;
  e.preventDefault();
  if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
  target.focus({ preventScroll: true });
  const header = document.querySelector('.site-header');
  const sticky = header && getComputedStyle(header).position === 'sticky';
  const headerHeight = sticky ? header.getBoundingClientRect().height : 0;
  const top = container.getBoundingClientRect().top + window.scrollY - headerHeight;
  window.scrollTo({ top: Math.max(0, top), left: 0, behavior: 'instant' });
}

function App() {
  const { theme, setTheme } = useTheme();
  const a11y = useAccessibility();
  const cookies = useCookieConsent();
  useDocumentLang();
  const { ui } = useLanguage();
  const en = languages.en;
  const cy = languages.cy;
  const gd = languages.gd;

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-dark-950">
      {/* Where focus starts on every page load: just before "Skip to main
          content", so the link stays out of sight until the first Tab
          (see ScrollToTop). */}
      <div id="page-start" tabIndex={-1} />
      <a href="#main-content" className="skip-link" onClick={skipToMain}>{ui.skipToContent}</a>
      <LanguageOverHistory />
      <ScrollToTop />
      <Header theme={theme} onSetTheme={setTheme} a11y={a11y} />

      <div id="main-content" tabIndex={-1} className="flex-1 focus:outline-none">
        <ErrorBoundary>
        <Routes>
          {/* Root → the language chosen in the menu, else the default locale */}
          <Route path="/" element={<Navigate to={`/${getPreferredLanguage() ?? defaultLanguage}`} replace />} />

          {/* Collections — one locale-parameterised route pair serves every
              language: /:lang/ukwa/collection[/:collectionId]. The page itself
              validates :lang and derives all its links from it. */}
          <Route path="/:lang/ukwa/collection" element={<CollectionsPage />} />
          <Route path="/:lang/ukwa/collection/:collectionId" element={<CollectionsPage />} />

          {/* Legacy collection URLs → canonical /en/ukwa/collection */}
          <Route path="/themes/collections" element={<Navigate to="/en/ukwa/collection" replace />} />
          <Route path="/themes/collections/:collectionId" element={<LegacyCollectionRedirect />} />

          {/* Legacy nominate / Information URLs → canonical /:lang/ukwa/info */}
          <Route path="/:lang/save-website" element={<LegacyNominateRedirect />} />
          <Route path="/:lang/information" element={<LegacyInfoRedirect />} />
          <Route path="/:lang/information/:page" element={<LegacyInfoRedirect />} />

          {/* English routes */}
          <Route path="/en" element={<MarkdownPage lang="en" slug="" theme={theme} />} />
          <Route path="/en/themes" element={<MarkdownPage lang="en" slug="themes" theme={theme} />} />
          <Route path="/en/ukwa/info/nominate" element={<MarkdownPage lang="en" slug="save-website" theme={theme} />} />
          <Route path="/en/contact" element={<MarkdownPage lang="en" slug="contact" theme={theme} />} />

          {/* About Us — breadcrumb shows Home / Information / About Us */}
          <Route path="/en/about" element={<MarkdownPage lang="en" slug="about" theme={theme} parentBreadcrumbs={infoCrumbs(en)} />} />

          {/* Information section */}
          <Route path="/en/ukwa/info" element={<InformationIndexPage langCode="en" />} />
          <Route path="/en/ukwa/info/faq" element={<MarkdownPage lang="en" slug="information/faq" theme={theme} parentBreadcrumbs={infoCrumbs(en)} />} />
          <Route path="/en/ukwa/info/accessibility" element={<MarkdownPage lang="en" slug="information/accessibility" theme={theme} parentBreadcrumbs={infoCrumbs(en)} />} />
          <Route path="/en/ukwa/info/terms" element={<MarkdownPage lang="en" slug="information/terms" theme={theme} parentBreadcrumbs={infoCrumbs(en)} />} />
          <Route path="/en/ukwa/info/technical-information" element={<MarkdownPage lang="en" slug="information/technical-information" theme={theme} parentBreadcrumbs={infoCrumbs(en)} />} />
          <Route path="/en/ukwa/info/notice-and-takedown" element={<MarkdownPage lang="en" slug="information/notice-and-takedown" theme={theme} parentBreadcrumbs={infoCrumbs(en)} />} />
          <Route path="/en/ukwa/info/sitemap" element={<SitemapPage langCode="en" />} />

          {/* Welsh routes */}
          <Route path="/cy" element={<MarkdownPage lang="cy" slug="" theme={theme} />} />
          <Route path="/cy/themes" element={<MarkdownPage lang="cy" slug="themes" theme={theme} />} />
          <Route path="/cy/ukwa/info/nominate" element={<MarkdownPage lang="cy" slug="save-website" theme={theme} />} />
          <Route path="/cy/contact" element={<MarkdownPage lang="cy" slug="contact" theme={theme} />} />
          <Route path="/cy/about" element={<MarkdownPage lang="cy" slug="about" theme={theme} parentBreadcrumbs={infoCrumbs(cy)} />} />
          <Route path="/cy/ukwa/info" element={<InformationIndexPage langCode="cy" />} />
          <Route path="/cy/ukwa/info/faq" element={<MarkdownPage lang="cy" slug="information/faq" theme={theme} parentBreadcrumbs={infoCrumbs(cy)} />} />
          <Route path="/cy/ukwa/info/accessibility" element={<MarkdownPage lang="cy" slug="information/accessibility" theme={theme} parentBreadcrumbs={infoCrumbs(cy)} />} />
          <Route path="/cy/ukwa/info/terms" element={<MarkdownPage lang="cy" slug="information/terms" theme={theme} parentBreadcrumbs={infoCrumbs(cy)} />} />
          <Route path="/cy/ukwa/info/technical-information" element={<MarkdownPage lang="cy" slug="information/technical-information" theme={theme} parentBreadcrumbs={infoCrumbs(cy)} />} />
          <Route path="/cy/ukwa/info/notice-and-takedown" element={<MarkdownPage lang="cy" slug="information/notice-and-takedown" theme={theme} parentBreadcrumbs={infoCrumbs(cy)} />} />
          <Route path="/cy/ukwa/info/sitemap" element={<SitemapPage langCode="cy" />} />

          {/* Gaelic routes */}
          <Route path="/gd" element={<MarkdownPage lang="gd" slug="" theme={theme} />} />
          <Route path="/gd/themes" element={<MarkdownPage lang="gd" slug="themes" theme={theme} />} />
          <Route path="/gd/ukwa/info/nominate" element={<MarkdownPage lang="gd" slug="save-website" theme={theme} />} />
          <Route path="/gd/contact" element={<MarkdownPage lang="gd" slug="contact" theme={theme} />} />
          <Route path="/gd/about" element={<MarkdownPage lang="gd" slug="about" theme={theme} parentBreadcrumbs={infoCrumbs(gd)} />} />
          <Route path="/gd/ukwa/info" element={<InformationIndexPage langCode="gd" />} />
          <Route path="/gd/ukwa/info/faq" element={<MarkdownPage lang="gd" slug="information/faq" theme={theme} parentBreadcrumbs={infoCrumbs(gd)} />} />
          {/* No /gd/ukwa/info/accessibility route: the Gaelic statement is hidden until its translation
              is approved, so the URL falls through to the Gaelic 404. Draft: docs/translations-pending/. */}
          <Route path="/gd/ukwa/info/terms" element={<MarkdownPage lang="gd" slug="information/terms" theme={theme} parentBreadcrumbs={infoCrumbs(gd)} />} />
          <Route path="/gd/ukwa/info/technical-information" element={<MarkdownPage lang="gd" slug="information/technical-information" theme={theme} parentBreadcrumbs={infoCrumbs(gd)} />} />
          <Route path="/gd/ukwa/info/notice-and-takedown" element={<MarkdownPage lang="gd" slug="information/notice-and-takedown" theme={theme} parentBreadcrumbs={infoCrumbs(gd)} />} />
          <Route path="/gd/ukwa/info/sitemap" element={<SitemapPage langCode="gd" />} />

          {/* Fallback: known legacy unprefixed URLs → canonical /en/…;
              everything else → a proper 404 (never a forward to home). */}
          <Route path="*" element={<LocaleFallback />} />
        </Routes>
        </ErrorBoundary>
      </div>

      {/* One contentinfo landmark for the whole footer area, so the footer
          navigation and partner logos are inside a landmark (WCAG 1.3.1 / axe
          "region"). */}
      <footer>
        <FooterMenu onManageCookies={cookies.openBanner} />
        <FooterLogos />
        <Footer />
      </footer>

      <CookieBanner
        open={cookies.bannerOpen}
        onAccept={() => { cookies.accept(); cookies.closeBanner(); }}
        onReject={() => { cookies.reject(); cookies.closeBanner(); }}
        onClose={cookies.closeBanner}
      />
    </div>
  );
}

export default App;
